<template>
    <div class="min-h-screen bg-gray-50 p-6">
        <div class="max-w-6xl mx-auto">
            <!-- 页面标题 -->
            <div class="mb-8">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-900 mb-2">历史对话</h1>
                        <p class="text-gray-600">查看和管理您的历史对话记录</p>
                    </div>
                    <button
                        @click="goBack"
                        class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-2"
                    >
                        <i class="fas fa-arrow-left"></i>
                        <span>返回主界面</span>
                    </button>
                </div>
            </div>

            <!-- 搜索和操作栏 -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <!-- 搜索框 -->
                    <div class="flex-1 max-w-md">
                        <div class="relative">
                            <input
                                v-model="searchKeyword"
                                @input="handleSearch"
                                type="text"
                                placeholder="搜索对话标题..."
                                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="flex flex-wrap gap-3">
                        <button
                            @click="loadConversations"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-sync-alt"></i>
                            <span>刷新</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- 统计信息 -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-blue-100 rounded-full">
                            <i class="fas fa-comments text-blue-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">总对话数</p>
                            <p class="text-2xl font-bold text-gray-900">{{ totalConversations }}</p>
                        </div>
                    </div>
                </div>
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-green-100 rounded-full">
                            <i class="fas fa-eye text-green-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">显示对话数</p>
                            <p class="text-2xl font-bold text-gray-900">{{ conversations.length }}</p>
                        </div>
                    </div>
                </div>
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-purple-100 rounded-full">
                            <i class="fas fa-clock text-purple-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">最新更新时间</p>
                            <p class="text-sm font-bold text-gray-900">{{ lastUpdateTime }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 对话列表 -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200">
                <div class="p-6 border-b border-gray-200">
                    <h2 class="text-lg font-semibold text-gray-900">对话列表</h2>
                </div>

                <!-- 加载状态 -->
                <div v-if="loading" class="flex items-center justify-center py-12">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span class="ml-2 text-gray-500">加载中...</span>
                </div>

                <!-- 空状态 -->
                <div v-else-if="conversations.length === 0" class="text-center py-12">
                    <i class="fas fa-comments text-6xl text-gray-300 mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">暂无历史对话</h3>
                    <p class="text-gray-500 mb-4">开始对话后，您的聊天记录将自动保存</p>
                </div>

                <!-- 对话列表 -->
                <div v-else class="divide-y divide-gray-200">
                    <div
                        v-for="conversation in conversations"
                        :key="conversation.id"
                        class="p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                        <div class="flex items-start justify-between">
                            <div class="flex-1 cursor-pointer" @click="loadConversation(conversation)">
                                <h3 class="text-lg font-medium text-gray-900 mb-2">
                                    {{ decodeUnicode(conversation.title) }}
                                </h3>
                                <div class="flex items-center space-x-4 text-sm text-gray-500">
                                    <span>
                                        <i class="fas fa-comment-dots mr-1"></i>
                                        {{ conversation.messageCount }} 条消息
                                    </span>
                                    <span>
                                        <i class="fas fa-calendar mr-1"></i>
                                        {{ formatDate(conversation.createdAt) }}
                                    </span>
                                    <span v-if="conversation.updatedAt !== conversation.createdAt">
                                        <i class="fas fa-edit mr-1"></i>
                                        更新于 {{ formatDate(conversation.updatedAt) }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex items-center space-x-2 ml-4">
                                <button
                                    @click="viewConversation(conversation)"
                                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
                                    title="查看对话内容"
                                >
                                    <i class="fas fa-eye"></i>
                                    <span>查看</span>
                                </button>
                                <button
                                    @click="loadConversation(conversation)"
                                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                                    title="加载到聊天界面"
                                >
                                    <i class="fas fa-external-link-alt"></i>
                                    <span>加载</span>
                                </button>
                                <button
                                    @click="deleteConversation(conversation)"
                                    class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
                                    title="删除对话"
                                >
                                    <i class="fas fa-trash"></i>
                                    <span>删除</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 分页 -->
                <div v-if="totalPages > 1" class="p-6 border-t border-gray-200">
                    <div class="flex items-center justify-between">
                        <div class="text-sm text-gray-700">
                            显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalConversations) }} 条，共 {{ totalConversations }} 条
                        </div>
                        <div class="flex items-center space-x-2">
                            <button
                                @click="goToPage(currentPage - 1)"
                                :disabled="currentPage <= 1"
                                class="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                上一页
                            </button>
                            <span class="px-3 py-1 text-sm text-gray-700">
                                第 {{ currentPage }} 页，共 {{ totalPages }} 页
                            </span>
                            <button
                                @click="goToPage(currentPage + 1)"
                                :disabled="currentPage >= totalPages"
                                class="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                下一页
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 对话内容查看弹窗 -->
        <div v-if="showViewDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
                <!-- 弹窗头部 -->
                <div class="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 class="text-xl font-semibold text-gray-900">
                        {{ decodeUnicode(selectedConversation?.title) }}
                    </h3>
                    <button
                        @click="closeViewDialog"
                        class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        <i class="fas fa-times text-xl"></i>
                    </button>
                </div>

                <!-- 弹窗内容 -->
                <div class="flex-1 overflow-y-auto p-6">
                    <!-- 加载状态 -->
                    <div v-if="loadingMessages" class="flex items-center justify-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span class="ml-2 text-gray-500">加载对话内容中...</span>
                    </div>

                    <!-- 对话消息 -->
                    <div v-else-if="conversationMessages.length > 0" class="space-y-4">
                        <div
                            v-for="message in conversationMessages"
                            :key="message.id"
                            :class="[
                                'flex',
                                message.messageType === 'QUESTION' ? 'justify-end' : 'justify-start'
                            ]"
                        >
                            <div
                                :class="[
                                    'max-w-[80%] rounded-lg px-4 py-2',
                                    message.messageType === 'QUESTION'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-900'
                                ]"
                            >
                                <div class="text-sm font-medium mb-1">
                                    {{ message.messageType === 'QUESTION' ? '您' : 'AI助手' }}
                                </div>
                                <div class="whitespace-pre-wrap">{{ decodeUnicode(message.content) }}</div>
                                <div class="text-xs mt-1 opacity-75">
                                    {{ formatTime(message.createdAt) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 空状态 -->
                    <div v-else class="text-center py-8">
                        <i class="fas fa-comments text-4xl text-gray-300 mb-4"></i>
                        <p class="text-gray-500">该对话暂无消息内容</p>
                    </div>
                </div>

                <!-- 弹窗底部 -->
                <div class="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div class="text-sm text-gray-500">
                        共 {{ conversationMessages.length }} 条消息
                    </div>
                    <div class="flex items-center space-x-3">
                        <button
                            @click="loadConversation(selectedConversation)"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-external-link-alt"></i>
                            <span>加载到聊天界面</span>
                        </button>
                        <button
                            @click="closeViewDialog"
                            class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
                        >
                            关闭
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HistoryConversations',
    data() {
        return {
            conversations: [],
            loading: false,
            searchKeyword: '',
            currentPage: 1,
            pageSize: 10,
            totalConversations: 0,
            totalPages: 0,
            lastUpdateTime: '',
            // 弹窗相关数据
            showViewDialog: false,
            selectedConversation: null,
            conversationMessages: [],
            loadingMessages: false
        }
    },
    mounted() {
        this.loadConversations()
    },
    methods: {
        // 返回主界面
        goBack() {
            this.$router.push('/chat')
        },
        
        // 加载对话列表
        async loadConversations() {
            try {
                this.loading = true
                const token = localStorage.getItem('token')
                if (!token) {
                    alert('用户未登录')
                    this.$router.push('/login')
                    return
                }
                
                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/history-conversations?page=${this.currentPage - 1}&size=${this.pageSize}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
                
                const result = await response.json()
                if (result.code === 200) {
                    this.conversations = result.data || []
                    this.totalConversations = result.totalElements || this.conversations.length
                    this.totalPages = result.totalPages || 1
                    
                    // 计算最新更新时间
                    if (this.conversations.length > 0) {
                        const latestConversation = this.conversations[0]
                        this.lastUpdateTime = this.formatDate(latestConversation.updatedAt)
                    }
                } else {
                    alert(result.message || '加载对话列表失败')
                }
            } catch (error) {
                console.error('加载对话列表失败:', error)
                alert('加载对话列表失败')
            } finally {
                this.loading = false
            }
        },
        
        // 搜索对话
        async handleSearch() {
            if (this.searchKeyword.trim()) {
                try {
                    this.loading = true
                    const token = localStorage.getItem('token')
                    if (!token) {
                        alert('用户未登录')
                        this.$router.push('/login')
                        return
                    }
                    
                    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/history-conversations/search?keyword=${encodeURIComponent(this.searchKeyword)}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    })
                    
                    const result = await response.json()
                    if (result.code === 200) {
                        this.conversations = result.data || []
                        this.totalConversations = this.conversations.length
                        this.totalPages = 1
                        this.currentPage = 1
                    } else {
                        alert(result.message || '搜索失败')
                    }
                } catch (error) {
                    console.error('搜索失败:', error)
                    alert('搜索失败')
                } finally {
                    this.loading = false
                }
            } else {
                this.loadConversations()
            }
        },
        
        // 跳转到指定页
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
                this.loadConversations()
            }
        },
        
        // 查看对话内容
        async viewConversation(conversation) {
            this.selectedConversation = conversation
            this.showViewDialog = true
            this.conversationMessages = []
            this.loadingMessages = true
            
            try {
                const token = localStorage.getItem('token')
                if (!token) {
                    alert('用户未登录')
                    this.$router.push('/login')
                    return
                }
                
                // 获取对话的详细消息内容
                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/history-conversations/${conversation.id}/messages`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
                
                const result = await response.json()
                if (result.code === 200) {
                    this.conversationMessages = result.data || []
                } else {
                    alert(result.message || '加载对话内容失败')
                }
            } catch (error) {
                console.error('加载对话内容失败:', error)
                alert('加载对话内容失败')
            } finally {
                this.loadingMessages = false
            }
        },
        
        // 关闭查看弹窗
        closeViewDialog() {
            this.showViewDialog = false
            this.selectedConversation = null
            this.conversationMessages = []
        },
        
        // 加载对话
        async loadConversation(conversation) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    alert('用户未登录');
                    this.$router.push('/login');
                    return;
                }

                // 调用新的API端点来处理历史对话加载
                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/load-history-conversation`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        conversationId: conversation.id
                    })
                });

                const result = await response.json();
                
                if (result.code === 200 && result.data) {
                    const data = result.data;
                    
                    // 更新sessionId
                    localStorage.setItem('sessionId', data.sessionId);
                    
                    // 显示操作结果
                    alert(data.message || '对话已加载');
                    
                    // 刷新历史对话列表
                    this.loadConversations();
                    
                    // 触发父组件加载对话（传递消息数据）
                    this.$emit('load-conversation', {
                        ...conversation,
                        sessionId: data.sessionId,
                        messages: data.messages
                    });
                    
                    // 返回主界面
                    this.$router.push('/chat');
                } else {
                    alert(result.message || '加载对话失败');
                }
            } catch (error) {
                console.error('加载对话失败:', error);
                alert('加载对话失败');
            }
        },
        
        // 删除对话
        async deleteConversation(conversation) {
            try {
                const confirmed = confirm(`确定要删除对话"${this.decodeUnicode(conversation.title)}"吗？`)
                if (!confirmed) {
                    return
                }
                
                const token = localStorage.getItem('token')
                if (!token) {
                    alert('用户未登录')
                    this.$router.push('/login')
                    return
                }
                
                const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/history-conversations/${conversation.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
                
                const result = await response.json()
                if (result.code === 200) {
                    alert('删除成功')
                    this.loadConversations()
                } else {
                    alert(result.message || '删除失败')
                }
            } catch (error) {
                console.error('删除对话失败:', error)
                alert('删除失败')
            }
        },
        
        // 格式化日期
        formatDate(dateString) {
            if (!dateString) return ''
            
            try {
                const date = new Date(dateString)
                const now = new Date()
                const diffTime = Math.abs(now - date)
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                
                if (diffDays === 1) {
                    return '今天'
                } else if (diffDays === 2) {
                    return '昨天'
                } else if (diffDays <= 7) {
                    return `${diffDays - 1}天前`
                } else {
                    return date.toLocaleDateString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                }
            } catch (error) {
                console.error('日期格式化失败:', error)
                return dateString
            }
        },
        
        // 格式化时间（用于消息显示）
        formatTime(timestamp) {
            if (!timestamp) return ''
            
            try {
                const date = new Date(timestamp)
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            } catch (error) {
                console.error('时间格式化失败:', error)
                return timestamp
            }
        },
        
        // 解码Unicode字符串
        decodeUnicode(str) {
            if (!str) return ''
            
            try {
                // 处理Unicode编码的字符串
                return str.replace(/\\u[\dA-Fa-f]{4}/g, function(match) {
                    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
                })
            } catch (error) {
                console.error('Unicode解码失败:', error)
                return str // 如果解码失败，返回原字符串
            }
        }
    }
}
</script>

<style scoped>
/* 自定义样式 */
</style>
